import React, { useEffect, useRef, useState } from "react";

import { RsvpFieldError } from "../../api/rsvp";
import { phoneError as validatePhoneInput, toE164 } from "../../utils/phone";
import type { FormPayment, PayPreference, PromoterDisplayMode } from "../../types/rsvp";
import type { TierCountMap } from "../../types/rsvp";
import { matchPromoterByText, ZoniqTicketFormBody } from "../ticketTemplate/ZoniqTicketFormBody";
import {
  computeSelectionTotal,
  createDefaultSelection,
  formatTicketInr,
  selectionsToTierCounts,
  sumSelectionParty,
  type TicketSelectionRow,
  type TierOption,
} from "../ticketTemplate/zoniqTicketUtils";

export type PublicSignInValues = {
  name: string;
  phone: string;
  email?: string;
  tier_counts: TierCountMap;
  party_size: number;
  promoter?: string;
  promoter_id?: string;
  whatsapp_opt_in: boolean;
  /** Paid forms only: pay now on Zoniq or at the gate. */
  pay_preference?: PayPreference;
};

type Props = {
  tiers: TierOption[];
  includePromoter?: boolean;
  promoters?: { id: string; name: string }[];
  promoterDisplay?: PromoterDisplayMode;
  /** Guest List mode: ask party size only — no ticket tier picker or cost. */
  partySizeOnly?: boolean;
  /** Blocks submit until the promoter code matches the roster (Guest List). */
  requirePromoterMatch?: boolean;
  /** RSVP shows ticket prices; Guest List and Walk-in don't sell tickets, so they stay price-free. */
  showPricing?: boolean;
  submitLabel: string;
  /** How this form takes money. Free (default) keeps the old behaviour. */
  paymentMode?: FormPayment;
  pending?: boolean;
  onSubmit: (values: PublicSignInValues) => void;
  /** The last submit error; a 422 naming `phone` is shown inline under the number. */
  submitError?: unknown;
};

export const PublicTierSignInForm: React.FC<Props> = ({
  tiers,
  includePromoter = false,
  promoters = [],
  promoterDisplay = "collapsible",
  partySizeOnly = false,
  requirePromoterMatch = false,
  showPricing = false,
  submitLabel,
  paymentMode = "free",
  pending = false,
  onSubmit,
  submitError,
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [promoter, setPromoter] = useState("");
  const [promoterId, setPromoterId] = useState("");
  const [selections, setSelections] = useState<TicketSelectionRow[]>(() => [createDefaultSelection(tiers)]);
  const [promoterForceTouched, setPromoterForceTouched] = useState(false);
  // Off by default: WhatsApp messages need the guest's explicit consent.
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [phoneCheck, setPhoneCheck] = useState<string | null>(null);
  const [emailCheck, setEmailCheck] = useState<string | null>(null);
  const [payChoice, setPayChoice] = useState<PayPreference>(paymentMode === "gate" ? "gate" : "online");

  useEffect(() => {
    setSelections((prev) => {
      if (prev.length === 0) return [createDefaultSelection(tiers)];
      return prev.map((sel) => ({
        ...sel,
        tierId: sel.tierId && tiers.some((t) => t.id === sel.tierId) ? sel.tierId : tiers[0]?.id ?? null,
      }));
    });
  }, [tiers]);

  const hasPromoterRoster = includePromoter && promoters.length > 0;
  const collectsPartySize = tiers.length > 0 || partySizeOnly;
  const partySize = collectsPartySize ? sumSelectionParty(selections) : 1;
  const submitLock = useRef(false);
  const isPaid = paymentMode !== "free";
  // The pay choice only exists when the host allows both; otherwise the form mode decides.
  const effectivePay: PayPreference | undefined = !isPaid
    ? undefined
    : paymentMode === "online_or_gate"
      ? payChoice
      : (paymentMode as PayPreference);
  const total = computeSelectionTotal(selections, tiers);
  const label =
    effectivePay === "online" && total > 0 ? `Continue to pay ${formatTicketInr(total)}` : submitLabel;

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (pending || submitLock.current) return;
    const invalidPhone = validatePhoneInput(phone);
    if (invalidPhone) {
      setPhoneCheck(invalidPhone);
      return;
    }
    if (collectsPartySize && partySize < 1) return;
    if (effectivePay === "online" && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setEmailCheck("Enter your email so we can send your receipt and pass.");
      return;
    }
    const matchedPromoter = hasPromoterRoster ? matchPromoterByText(promoter, promoters) : null;
    // Roster is exact-match validated: a non-blank code that doesn't match a
    // known promoter is rejected rather than silently dropped or free-typed in.
    if (hasPromoterRoster && promoter.trim() && !matchedPromoter) {
      setPromoterForceTouched(true);
      return;
    }
    // Guest List: the code is mandatory, so a blank or unmatched one blocks.
    if (requirePromoterMatch && !matchedPromoter) {
      setPromoterForceTouched(true);
      return;
    }
    const tierCounts = partySizeOnly || tiers.length === 0 ? {} : selectionsToTierCounts(selections);
    submitLock.current = true;
    onSubmit({
      name: name.trim(),
      phone: toE164(phone) ?? phone.trim(),
      email: email.trim() || undefined,
      tier_counts: tierCounts,
      party_size: Math.max(1, partySize),
      promoter: hasPromoterRoster ? matchedPromoter?.name : includePromoter ? promoter.trim() : undefined,
      promoter_id: hasPromoterRoster ? matchedPromoter?.id : undefined,
      whatsapp_opt_in: whatsappOptIn,
      pay_preference: effectivePay,
    });
  };

  useEffect(() => {
    if (!pending) submitLock.current = false;
  }, [pending]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <ZoniqTicketFormBody
        tiers={tiers}
        selections={selections}
        onSelectionsChange={setSelections}
        name={name}
        phone={phone}
        email={email}
        onNameChange={setName}
        onPhoneChange={(value) => {
          setPhone(value);
          setPhoneCheck(null);
        }}
        phoneError={phoneCheck ?? (submitError instanceof RsvpFieldError ? (submitError.fieldErrors.phone ?? null) : null)}
        extraFields={
          <>
            {paymentMode === "online_or_gate" ? (
              <fieldset className="zticket-field" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="zticket-field__label">HOW WOULD YOU LIKE TO PAY?</legend>
                <label className="zticket-check">
                  <input type="radio" name="pay-choice" checked={payChoice === "online"} onChange={() => setPayChoice("online")} />
                  <span>Pay now (UPI, card, netbanking)</span>
                </label>
                <label className="zticket-check">
                  <input type="radio" name="pay-choice" checked={payChoice === "gate"} onChange={() => setPayChoice("gate")} />
                  <span>Pay at the gate</span>
                </label>
              </fieldset>
            ) : null}
            {effectivePay === "gate" ? (
              <p className="zticket-hint">You&apos;ll pay at the gate. Show your pass there to complete entry.</p>
            ) : null}
            {emailCheck ? <p className="zticket-hint" role="alert" style={{ color: "#f87171" }}>{emailCheck}</p> : null}
            <label className="zticket-check">
              <input type="checkbox" checked={whatsappOptIn} onChange={(e) => setWhatsappOptIn(e.target.checked)} />
              <span>Send my pass and event updates on WhatsApp</span>
            </label>
          </>
        }
        onEmailChange={(value) => {
          setEmail(value);
          setEmailCheck(null);
        }}
        emailRequired={effectivePay === "online"}
        partySizeOnly={partySizeOnly}
        includePromoter={includePromoter}
        promoterDisplay={promoterDisplay}
        promoterRequired={requirePromoterMatch}
        promoters={promoters}
        promoterId={promoterId}
        promoterText={promoter}
        onPromoterIdChange={setPromoterId}
        onPromoterTextChange={setPromoter}
        promoterForceTouched={promoterForceTouched}
        showPricing={showPricing}
        submitLabel={label}
        pending={pending}
        showFooter={!isPaid}
      />
    </form>
  );
};
